import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // Knack: the persistent carousel "+" zoom button's aria-label needs updating as the user
    // swipes between slides (it can't carry a per-slide index the way the old per-slide button did).
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Knack: keeps the persistent gallery zoom button's aria-label in sync with the active slide.
   * SlideshowSelectEvent (assets/events.js) fires on every user-driven slide change, including a
   * plain scroll/swipe (not just programmatic selection). Labels are pre-translated server-side
   * and passed through as JSON (data-zoom-labels) so nothing here hardcodes translated copy.
   *
   * @param {SlideshowSelectEvent} event - The slideshow:select event.
   */
  #handleSlideSelect = (event) => {
    // The button's `ref` is captured by the nearest enclosing custom element, which is
    // slideshow-component (not media-gallery) since the button lives inside slideshow-container -
    // reached via this.slideshow.refs rather than this.refs.
    const button = this.slideshow?.refs?.galleryZoomButton;
    const label = this.#zoomLabels?.[event.detail.index];

    if (button && label) button.setAttribute('aria-label', label);
  };

  /**
   * Knack: parses the persistent gallery zoom button's data-zoom-labels JSON (one pre-translated
   * label per media index), if present.
   *
   * @returns {string[] | null}
   */
  get #zoomLabels() {
    const raw = this.slideshow?.refs?.galleryZoomButton?.dataset.zoomLabels;

    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Knack: zooms the CURRENTLY VISIBLE slide. Used by the persistent carousel zoom button
   * (rendered once, not once per slide - see product-media-gallery-content.liquid), which can't
   * carry a hardcoded slide index the way the per-slide grid zoom button (`zoom` above) does.
   * Passes the actual current slide element through as the view-transition source so the zoom
   * still morphs from the visible image instead of falling back to an instant open (zoom-dialog.js
   * normally derives that source from the clicked element's closest slide/li, which doesn't apply
   * to a button that no longer lives inside a slide).
   *
   * @param {PointerEvent} event - The pointer event.
   */
  zoomActive(event) {
    const index = this.slideshow?.current ?? 0;
    const sourceSlide = this.slideshow?.slides?.[index];

    this.refs.zoomDialogComponent?.open(index, event, sourceSlide);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
